import { errorMsg } from '../../utils/errorMsg';
import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Box, IconButton, Button, Chip,
  CircularProgress, Alert, Paper, Tabs, Tab, Pagination, Divider,
} from '@mui/material';
import {
  Delete as DeleteIcon, MarkEmailRead as MarkEmailReadIcon,
  Notifications as NotificationsIcon, Refresh,
} from '@mui/icons-material';
import Header from '../../components/Header';
import api from '../../axios';

const PURPLE = '#00F1A7';

function NotificacoesCentro() {
  const [notificacoes, setNotificacoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [total, setTotal] = useState(0);
  const [naoLidas, setNaoLidas] = useState(0);
  const [page, setPage] = useState(0);
  const [filtro, setFiltro] = useState(0);
  const limit = 30;

  const carregar = async () => {
    setLoading(true);
    setErro('');
    try {
      const params = { limit, offset: page * limit };
      if (filtro === 1) params.nao_lidas = true;
      const res = await api.get('/notificacoes/todas', { params });
      setNotificacoes(res.data?.notificacoes || []);
      setTotal(res.data?.total || 0);
      setNaoLidas(res.data?.nao_lidas || 0);
    } catch (err) {
      setErro(errorMsg(err) || 'Erro ao carregar notificações');
      setNotificacoes([]);
    }
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { carregar(); }, [page, filtro]);

  const marcarLida = async (id) => {
    try {
      await api.patch(`/notificacoes/${id}/ler`);
      setNotificacoes(prev => (prev || []).map(n => n.id === id ? { ...n, lida: true } : n));
      setNaoLidas(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const marcarTodasLidas = async () => {
    try {
      await api.patch('/notificacoes/ler-todas');
      setNotificacoes(prev => (prev || []).map(n => ({ ...n, lida: true })));
      setNaoLidas(0);
    } catch {}
  };

  const deletar = async (id) => {
    try {
      await api.delete(`/notificacoes/${id}`);
      setNotificacoes(prev => (prev || []).filter(n => n.id !== id));
      setTotal(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const getTipoColor = (tipo) => {
    const cores = {
      novo_membro: 'success', gc_criado: 'info', gc_aprovado: 'success',
      discipulado_iniciado: 'warning', migracao_etapa: 'primary',
      alerta_pastoral: 'error', curso_criado: 'default',
    };
    return cores[tipo] || 'default';
  };

  const getTipoLabel = (tipo) => {
    const labels = {
      novo_membro: 'Novo Membro', gc_criado: 'Novo GC', gc_aprovado: 'GC Aprovado',
      discipulado_iniciado: 'Discipulado', migracao_etapa: 'Migração',
      alerta_pastoral: 'Alerta', curso_criado: 'Curso',
    };
    return labels[tipo] || tipo;
  };

  const items = notificacoes || [];

  return (
    <>
      <Header />
      <Container maxWidth="md" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, md: 2 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, fontSize: { xs: '1.2rem', md: '1.5rem' } }}>
              <NotificationsIcon sx={{ mr: 1, verticalAlign: 'middle', color: PURPLE }} />
              Notificações
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {total} total · {naoLidas || 0} não lidas
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" startIcon={<Refresh />} onClick={carregar}>Atualizar</Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<MarkEmailReadIcon />}
              onClick={marcarTodasLidas}
              disabled={!naoLidas}
            >
              Marcar todas lidas
            </Button>
          </Box>
        </Box>

        {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}

        <Tabs value={filtro} onChange={(_, v) => { setFiltro(v); setPage(0); }} sx={{ mb: 2 }}>
          <Tab label="Todas" />
          <Tab label={`Não lidas (${naoLidas || 0})`} />
        </Tabs>

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <CircularProgress size={40} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Carregando notificações...
            </Typography>
          </Box>
        ) : items.length === 0 ? (
          <Paper sx={{ borderRadius: 2, p: 6, textAlign: 'center' }}>
            <NotificationsIcon sx={{ fontSize: 56, mb: 2, opacity: 0.2, color: PURPLE }} />
            <Typography variant="h6" sx={{ color: '#999' }}>Nenhuma notificação</Typography>
            <Typography variant="body2" color="text.secondary">
              {filtro === 0 ? 'Suas notificações aparecerão aqui' : 'Nenhuma notificação não lida'}
            </Typography>
          </Paper>
        ) : (
          <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
            {items.map((n, idx) => (
              <React.Fragment key={n.id}>
                <Box
                  sx={{
                    p: { xs: 1.5, md: 2 },
                    display: 'flex', alignItems: 'flex-start', gap: 1.5,
                    bgcolor: n.lida ? 'transparent' : `${PURPLE}06`,
                    transition: 'background 0.2s',
                    '&:hover': { bgcolor: n.lida ? 'action.hover' : `${PURPLE}0C` },
                  }}
                >
                  <Chip
                    label={getTipoLabel(n.tipo)}
                    color={getTipoColor(n.tipo)}
                    size="small"
                    variant={n.lida ? 'outlined' : 'filled'}
                    sx={{ mt: 0.3, flexShrink: 0 }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: n.lida ? 400 : 600, wordBreak: 'break-word' }}
                    >
                      {n.mensagem}
                    </Typography>
                    {n.data_criacao && (
                      <Typography variant="caption" color="text.secondary">
                        {new Date(n.data_criacao).toLocaleString('pt-BR')}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.3, flexShrink: 0 }}>
                    {!n.lida && (
                      <IconButton size="small" onClick={() => marcarLida(n.id)} title="Marcar lida">
                        <MarkEmailReadIcon fontSize="small" />
                      </IconButton>
                    )}
                    <IconButton size="small" onClick={() => deletar(n.id)} title="Excluir">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
                {idx < items.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </Paper>
        )}

        {total > limit && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination
              count={Math.ceil(total / limit)}
              page={page + 1}
              onChange={(_, p) => setPage(p - 1)}
              color="primary"
            />
          </Box>
        )}
      </Container>
    </>
  );
}

export default NotificacoesCentro;
